import { MARKETPLACE_SHORTLIST_MAX } from '@crm/contracts';

/** The website the marketplace lives on, if nothing configures another. */
export const DEFAULT_WEBSITE_URL = 'https://remoteleverage.com';

/**
 * The public website's address: the environment's `/config.js` (`websiteUrl`, written by the
 * release from the WEBSITE_URL variable) or, locally, `VITE_WEBSITE_URL`; else the live site.
 */
export function websiteUrl(
  env: ImportMetaEnv = import.meta.env,
  runtime: CrmRuntimeConfig | undefined = window.__CRM_CONFIG__,
): string {
  return (runtime?.websiteUrl || env.VITE_WEBSITE_URL || DEFAULT_WEBSITE_URL).replace(/\/+$/, '');
}

export type MarketplaceShortlist =
  | { url: string; codes: string[]; missing: number }
  | { url: null; reason: string; codes: string[]; missing: number };

/**
 * A shortlist as the website's marketplace shows it: `/marketplace/shortlist/?candidates=A,B`
 * (the website's `MarketplaceLink::shortlist`), one card per marketplace code, in shortlist
 * order, each once. Candidates without a code are left out (`missing`). Over the website's limit
 * there is no link at all: a link to only the first ones would show a client part of the
 * shortlist as if it were all of it.
 */
export function marketplaceShortlist(
  candidates: ReadonlyArray<{ candidate: { marketplaceCode: string | null } }>,
  base: string = websiteUrl(),
): MarketplaceShortlist {
  const codes = [
    ...new Set(candidates.map((c) => c.candidate.marketplaceCode).filter((c): c is string => !!c)),
  ];
  const missing = candidates.filter((c) => !c.candidate.marketplaceCode).length;
  if (codes.length === 0)
    return {
      url: null,
      reason:
        candidates.length === 0
          ? 'No candidates on this shortlist yet'
          : 'No candidate on this shortlist has a marketplace code',
      codes,
      missing,
    };
  if (codes.length > MARKETPLACE_SHORTLIST_MAX)
    return {
      url: null,
      reason: `The marketplace shows at most ${MARKETPLACE_SHORTLIST_MAX} candidates; this shortlist has ${codes.length}`,
      codes,
      missing,
    };
  // Codes match MARKETPLACE_CODE_PATTERN (letters, digits, _ and -), so the commas stay literal
  // as the website writes them.
  return { url: marketplaceShortlistUrl(codes, base), codes, missing };
}

/** The marketplace link of exactly these codes, in this order (the picker's ticked ones). */
export function marketplaceShortlistUrl(codes: readonly string[], base: string = websiteUrl()) {
  return `${base}/marketplace/shortlist/?candidates=${codes.join(',')}`;
}

/**
 * One candidate on the website's marketplace: `/marketplace/?candidate=RL-X` (the website's
 * `MarketplaceLink::candidate`) opens the marketplace on that candidate with their profile open.
 * Null without a code.
 */
export function marketplaceCandidate(
  code: string | null,
  base: string = websiteUrl(),
): string | null {
  return code ? `${base}/marketplace/?candidate=${encodeURIComponent(code)}` : null;
}
